/**
 * Camera-kind lookup for grading. CameraFrame only carries `camId`, so the
 * grader / booth / bot need a way to map ids to kinds. Callers should pass the
 * real kit (`camKindFromKit(kit)`); `inferCamKind` is a fallback that understands
 * the default football kit ids ("CAM 1".."CAM 9", "BENCH", "CROWD", ...).
 */
import type { CameraKind, ShotSize } from '../core/contracts';

export type CamKindOf = (camId: string) => CameraKind | null;

/** Default football kit order — matches render/cameras/kit.ts and the design doc. */
export const DEFAULT_KIT_KINDS: readonly CameraKind[] = [
  'main_wide', 'main_tight', 'high_18_left', 'high_18_right',
  'behind_goal_left', 'behind_goal_right',
  'pitchside_handheld_left', 'pitchside_handheld_right', 'reverse_angle',
];

const ALL_KINDS: readonly CameraKind[] = [
  ...DEFAULT_KIT_KINDS, 'bench', 'crowd', 'spidercam', 'blimp', 'super_slomo',
];

/** Best-effort kind from a camera id. */
export function inferCamKind(camId: string): CameraKind | null {
  const id = camId.trim().toLowerCase();
  const direct = ALL_KINDS.find((k) => k === id);
  if (direct) return direct;
  if (id.includes('bench') || id.includes('dugout')) return 'bench';
  if (id.includes('crowd') || id.includes('fans')) return 'crowd';
  if (id.includes('spider')) return 'spidercam';
  if (id.includes('blimp')) return 'blimp';
  if (id.includes('ssm') || id.includes('slo')) return 'super_slomo';
  const m = /(\d+)/.exec(id);
  if (m) return DEFAULT_KIT_KINDS[Number(m[1]) - 1] ?? null;
  return null;
}

/** Build a lookup from a camera kit (CameraDef[] or any {id, kind}[]). */
export function camKindFromKit(kit: readonly { id: string; kind: CameraKind }[]): CamKindOf {
  const map = new Map(kit.map((c) => [c.id, c.kind] as const));
  return (camId) => map.get(camId) ?? inferCamKind(camId);
}

export const isWideSize = (s: ShotSize | undefined): boolean => s === 'wide' || s === 'extreme_wide';
export const isCloseSize = (s: ShotSize | undefined): boolean => s === 'close' || s === 'extreme_close';

/** Camera kinds that are never a "covering" live shot. */
export const NON_GAME_KINDS: ReadonlySet<CameraKind> = new Set<CameraKind>(['bench', 'crowd']);
